import { GAME_CONFIG } from "../config/gameConfig.js";
import { getLevel, movePosOnWalkable } from "../systems/level.js";
import { spawnMovementDust } from "../systems/dust.js";
import { getRoomAt } from "../systems/rooms.js";
import {
    lockAlarmRoom,
    registerGuardRobotRoom,
    unregisterGuardRobotRoom,
    updateAlarmLockdowns,
} from "../systems/alarmLockdown.js";
import { isGameplayPaused } from "../systems/pauseState.js";
import { AUDIO_SOUNDS, playAt } from "../systems/audio.js";

function isRobotWorldPaused() {
    return isGameplayPaused();
}

/**
 * Guard Robot — KAPLAY level.getPath() navigation + health
 */

const WHEEL_BOT_SCALE = 2.5;
const WHEEL_BOT_WAKE_SECONDS = 0.5;
const WHEEL_BOT_HURT_LOCK = 0.28;
const WHEEL_BOT_ATTACK_LOCK = 0.32;
const WHEEL_BOT_SHOOT_LOCK = 0.32;
const WHEEL_BOT_DEATH_SECONDS = 1.5;
const TRAP_PELLET_LIFETIME = 1.2;
const TRAP_PELLET_RADIUS = 3;
const TRAP_PELLET_HIT_RADIUS = 12;
const ROBOT_Z = 9710;
const ROBOT_SHADOW_Z = 9709;
const ROBOT_DUST_INTERVAL = 0.09;
const ROBOT_DUST_Z = 9708;
const ROBOT_HITBOX_WIDTH = 14;
const ROBOT_HITBOX_HEIGHT = 14;
const WHEEL_BOT_FRAME_WIDTH = 117;
const WHEEL_BOT_BODY_CENTER_X = 25.5;
const WHEEL_BOT_DEATH_BODY_CENTER_X = 19.5;

const WHEEL_BOT_ANIMS = {
    sleep: { sprite: "wheel_bot_wake", anim: "sleep" },
    wake: { sprite: "wheel_bot_wake", anim: "wake" },
    idle: { sprite: "wheel_bot_move", anim: "idle" },
    move: { sprite: "wheel_bot_move", anim: "move" },
    charge: { sprite: "wheel_bot_charge", anim: "charge" },
    shoot: { sprite: "wheel_bot_shoot", anim: "shoot" },
    damaged: { sprite: "wheel_bot_damaged", anim: "damaged" },
    death: { sprite: "wheel_bot_death", anim: "death" },
};

function asVec(p) {
    if (!p) return null;
    if (typeof p.x === "number" && typeof p.y === "number") {
        return vec2(p.x, p.y);
    }
    return null;
}

function setRobotAnimation(robot, state) {
    if (!robot.visual?.exists?.()) return;

    const next = WHEEL_BOT_ANIMS[state] || WHEEL_BOT_ANIMS.idle;
    const facingLeft = robot.facing < 0;
    const visualOffsetX = getRobotVisualOffsetX(robot.facing);

    if (robot.visualState === state && robot.visual.flipX === facingLeft) {
        if (robot.animsFrozen) freezeRobotAnims(robot, true);
        return;
    }

    robot.visual.use(sprite(next.sprite, {
        anim: next.anim,
        flipX: facingLeft,
    }));
    robot.visual.flipX = facingLeft;
    robot.visual.pos = vec2(visualOffsetX, 0);
    if (robot.shadow?.exists?.()) {
        robot.shadow.use(sprite(next.sprite, {
            anim: next.anim,
            flipX: facingLeft,
            flipY: true,
        }));
        robot.shadow.flipX = facingLeft;
        robot.shadow.flipY = true;
        robot.shadow.pos = vec2(visualOffsetX, 18);
    }
    robot.visualState = state;
    if (robot.animsFrozen) freezeRobotAnims(robot, true);
}

function freezeRobotAnims(robot, frozen) {
    robot.animsFrozen = !!frozen;
    const speed = frozen ? 0 : 1;
    if (robot.visual?.exists?.()) robot.visual.animSpeed = speed;
    if (robot.shadow?.exists?.()) robot.shadow.animSpeed = speed;
}

function getRobotVisualOffsetX(facing, bodyCenterX = WHEEL_BOT_BODY_CENTER_X) {
    const frameCenterX = WHEEL_BOT_FRAME_WIDTH / 2;
    const offset = (frameCenterX - bodyCenterX) * WHEEL_BOT_SCALE;
    return facing < 0 ? -offset : offset;
}

function wakeRobotsInRoom(roomId, sourceRobot) {
    if (roomId == null) return;

    get("guard_robot", { recursive: true }).forEach((otherRobot) => {
        if (!otherRobot.exists?.() || otherRobot === sourceRobot) return;
        if (otherRobot.isActive || otherRobot.isWaking) return;

        const otherRoomId = otherRobot.alarmRoomId ?? getRoomAt(otherRobot.pos)?.id;
        if (otherRoomId !== roomId) return;

        if (otherRobot.activate) {
            otherRobot.activate({ propagate: false });
        }
    });
}

export function spawnGuardRobot(posVec, isInitiallyActive = false) {
    const cfg = GAME_CONFIG.guardRobot;
    const startPos = asVec(posVec) || vec2(0, 0);

    const robot = add([
        pos(startPos),
        anchor("center"),
        // Solid static body — blocks the player, but physics can't shove this character
        area({
            shape: new Rect(
                vec2(-ROBOT_HITBOX_WIDTH / 2, -ROBOT_HITBOX_HEIGHT / 2),
                ROBOT_HITBOX_WIDTH,
                ROBOT_HITBOX_HEIGHT,
            ),
        }),
        body({
            isStatic: true,
            collisionIgnore: ["human", "guard_robot"],
        }),
        health(cfg.maxHp),
        z(ROBOT_Z),
        "guard_robot",
        {
            isActive: isInitiallyActive,
            knockbackVelocity: vec2(0, 0),
            attackCooldown: 0,
            shootCooldown: 0,
            dustTimer: 0,
            repathTimer: 0,
            path: [],
            facing: 1,
            isWaking: false,
            wakeLock: 0,
            hurtLock: 0,
            attackLock: 0,
            shootLock: 0,
            visualState: isInitiallyActive ? "idle" : "sleep",
            alarmRoomId: null,
        },
    ]);

    const spawnRoom = getRoomAt(robot.pos);
    robot.alarmRoomId = spawnRoom?.id ?? null;
    registerGuardRobotRoom(robot.alarmRoomId);

    if (isInitiallyActive) {
        lockAlarmRoom(robot.alarmRoomId);
    }

    const initialAnim = isInitiallyActive ? WHEEL_BOT_ANIMS.idle : WHEEL_BOT_ANIMS.sleep;
    robot.shadow = robot.add([
        sprite(initialAnim.sprite, {
            anim: initialAnim.anim,
            flipY: true,
        }),
        pos(getRobotVisualOffsetX(1), 18),
        anchor("center"),
        scale(WHEEL_BOT_SCALE * 1.05, WHEEL_BOT_SCALE * 0.36),
        color(0, 0, 0),
        opacity(0.32),
        z(ROBOT_SHADOW_Z),
    ]);

    robot.visual = robot.add([
        sprite(initialAnim.sprite, { anim: initialAnim.anim }),
        pos(getRobotVisualOffsetX(1), 0),
        anchor("center"),
        scale(WHEEL_BOT_SCALE),
        color(255, 255, 255),
        z(ROBOT_Z),
    ]);

    robot.onCollideUpdate("player", (p) => {
        if (p.isDigging) return;
        robot.activate();
    });

    robot.activate = (options = {}) => {
        if (robot.isActive || robot.isWaking) return;
        const robotRoomId = robot.alarmRoomId ?? getRoomAt(robot.pos)?.id;
        robot.alarmRoomId = robotRoomId ?? null;
        lockAlarmRoom(robotRoomId);
        robot.isWaking = true;
        robot.wakeLock = WHEEL_BOT_WAKE_SECONDS;
        robot.path = [];
        setRobotAnimation(robot, "wake");
        playAt(AUDIO_SOUNDS.robotWake, robot.pos);
        if (options.propagate !== false && robotRoomId != null) {
            wakeRobotsInRoom(robotRoomId, robot);
        }
    };

    robot.takeDamage = (dmg) => {
        const willSurvive = robot.hp - dmg > 0;
        robot.hurtLock = WHEEL_BOT_HURT_LOCK;
        robot.attackLock = 0;
        robot.shootLock = 0;
        setRobotAnimation(robot, "damaged");
        if (dmg > 0 && willSurvive) {
            playAt(AUDIO_SOUNDS.robotHit, robot.pos, { volume: 0.55 });
        }
        robot.hp -= dmg;
    };

    robot.applyKnockback = (dirVec, force) => {
        robot.knockbackVelocity = dirVec.scale(force);
        robot.path = [];
    };

    robot.onHurt(() => {
        if (robot.hurtLock > 0) return;
        robot.hurtLock = WHEEL_BOT_HURT_LOCK;
        robot.attackLock = 0;
        robot.shootLock = 0;
        setRobotAnimation(robot, "damaged");
    });

    robot.onDeath(() => {
        playAt(AUDIO_SOUNDS.robotHit, robot.pos, { volume: 0.55 });
        unregisterGuardRobotRoom(robot.alarmRoomId);
        const deathFx = add([
            sprite(WHEEL_BOT_ANIMS.death.sprite, { anim: WHEEL_BOT_ANIMS.death.anim, flipX: robot.facing < 0 }),
            pos(robot.pos.add(vec2(getRobotVisualOffsetX(robot.facing, WHEEL_BOT_DEATH_BODY_CENTER_X), 0))),
            anchor("center"),
            scale(WHEEL_BOT_SCALE),
            color(255, 255, 255),
            z(95),
        ]);
        wait(WHEEL_BOT_DEATH_SECONDS, () => {
            if (deathFx.exists()) destroy(deathFx);
        });
        destroy(robot);
        updateAlarmLockdowns();
    });

    function refreshPath(targetPos) {
        const level = getLevel();
        const from = asVec(robot.pos);
        const to = asVec(targetPos);
        if (!level || !from || !to || typeof level.getPath !== "function") {
            robot.path = to ? [to] : [];
            return;
        }

        try {
            const path = level.getPath(from, to, { allowDiagonals: true });
            if (!Array.isArray(path) || path.length === 0) {
                robot.path = [to];
                return;
            }
            robot.path = path.map((p) => asVec(p)).filter(Boolean);
        } catch {
            robot.path = [to];
        }
    }

    function spawnTrapPellet(targetPos) {
        const start = vec2(robot.pos.x, robot.pos.y);
        const target = asVec(targetPos);
        if (!target) return;

        const shotDir = target.sub(start);
        if (shotDir.len() <= 0.1) return;

        const pellet = add([
            circle(TRAP_PELLET_RADIUS),
            pos(start.add(shotDir.unit().scale(10))),
            anchor("center"),
            color(255, 255, 255),
            opacity(0.95),
            z(9600),
            "trap_pellet",
            {
                age: 0,
                dir: shotDir.unit(),
            },
        ]);

        pellet.onUpdate(() => {
            if (!pellet.exists()) return;
            if (isRobotWorldPaused()) return;
            pellet.age += dt();
            pellet.pos = pellet.pos.add(pellet.dir.scale((cfg.pelletSpeed ?? 260) * dt()));

            const player = get("player")[0];
            if (player && !player.isDead && !player.isDigging && player.pos.dist(pellet.pos) <= TRAP_PELLET_HIT_RADIUS) {
                if (player.applyTrap) {
                    player.applyTrap(cfg.trapDuration ?? 0.5, cfg.trapDamage ?? 5);
                }
                destroy(pellet);
                return;
            }

            if (pellet.age >= TRAP_PELLET_LIFETIME) {
                destroy(pellet);
            }
        });
    }

    function emitRobotDust(moveDir) {
        if (!moveDir || moveDir.len() <= 0) return;
        robot.dustTimer -= dt();
        if (robot.dustTimer > 0) return;
        robot.dustTimer = ROBOT_DUST_INTERVAL;
        spawnMovementDust(robot.pos, moveDir, {
            footOffset: vec2(0, 6),
            z: ROBOT_DUST_Z,
            size: 2.2,
            opacity: 0.38,
            color: [92, 86, 78],
        });
    }

    onUpdate(() => {
        if (!robot.exists()) return;
        if (isRobotWorldPaused()) {
            freezeRobotAnims(robot, true);
            return;
        }
        freezeRobotAnims(robot, false);
        if (robot.hurtLock > 0) robot.hurtLock -= dt();
        if (robot.attackLock > 0) robot.attackLock -= dt();
        if (robot.shootLock > 0) robot.shootLock -= dt();
        if (robot.shootCooldown > 0) robot.shootCooldown -= dt();
        const canUseAmbientAnimation = robot.hurtLock <= 0 && robot.attackLock <= 0 && robot.shootLock <= 0;

        if (robot.isWaking) {
            robot.wakeLock -= dt();
            if (robot.wakeLock > 0) return;

            robot.isWaking = false;
            robot.wakeLock = 0;
            robot.isActive = true;
            robot.repathTimer = cfg.repathInterval || 0.30;
            return;
        }

        if (robot.knockbackVelocity.len() > 5) {
            const beforePos = vec2(robot.pos.x, robot.pos.y);
            if (Math.abs(robot.knockbackVelocity.x) > 1) {
                robot.facing = robot.knockbackVelocity.x < 0 ? -1 : 1;
            }
            if (canUseAmbientAnimation) setRobotAnimation(robot, "move");
            const delta = robot.knockbackVelocity.scale(dt());
            const slid = movePosOnWalkable(beforePos, delta);
            if (slid.moved) {
                robot.pos = slid.pos;
                emitRobotDust(robot.pos.sub(beforePos));
            }
            if (slid.hitWall) {
                if (robot.pos.x === beforePos.x) robot.knockbackVelocity.x = 0;
                if (robot.pos.y === beforePos.y) robot.knockbackVelocity.y = 0;
                if (!slid.moved) robot.knockbackVelocity = vec2(0, 0);
            }
            robot.knockbackVelocity = robot.knockbackVelocity.scale(0.85);
            return;
        }

        if (!robot.isActive) {
            if (canUseAmbientAnimation) setRobotAnimation(robot, "sleep");
            return;
        }

        const player = get("player")[0];
        const playerPos = player ? asVec(player.pos) : null;
        if (!player || !playerPos || player.isDigging) {
            if (canUseAmbientAnimation) setRobotAnimation(robot, "idle");
            return;
        }

        const robotPos = asVec(robot.pos);
        if (!robotPos) return;

        const robotRoom = getRoomAt(robotPos);
        const playerRoom = getRoomAt(playerPos);
        if (!robotRoom || !playerRoom || robotRoom.id !== playerRoom.id) {
            robot.path = [];
            if (canUseAmbientAnimation) setRobotAnimation(robot, "idle");
            return;
        }

        const distToPlayer = robotPos.dist(playerPos);
        const meleeDist = cfg.meleeRange || 22;
        const shootRange = cfg.shootRange || 150;
        const repathInt = cfg.repathInterval || 0.30;
        let didMove = false;
        let didShoot = false;
        const canShoot = robot.shootCooldown <= 0
            && robot.attackLock <= 0
            && robot.hurtLock <= 0
            && robot.shootLock <= 0
            && distToPlayer > meleeDist + 8
            && distToPlayer <= shootRange;

        if (canShoot) {
            if (Math.abs(playerPos.x - robotPos.x) > 1) {
                robot.facing = playerPos.x < robotPos.x ? -1 : 1;
            }
            robot.shootLock = WHEEL_BOT_SHOOT_LOCK;
            setRobotAnimation(robot, "shoot");
            spawnTrapPellet(playerPos);
            robot.shootCooldown = cfg.shootCooldown ?? 1.6;
            didShoot = true;
        }

        if (distToPlayer > meleeDist) {
            robot.repathTimer += dt();
            if (robot.repathTimer >= repathInt || robot.path.length === 0) {
                robot.repathTimer = 0;
                refreshPath(playerPos);
            }

            while (robot.path.length > 0 && robotPos.dist(robot.path[0]) < 10) {
                robot.path.shift();
            }

            if (robot.path.length > 0) {
                const next = asVec(robot.path[0]);
                if (next) {
                    const dir = next.sub(robotPos);
                    if (dir.len() > 0) {
                        if (Math.abs(dir.x) > 1) {
                            robot.facing = dir.x < 0 ? -1 : 1;
                        }
                        const beforePos = vec2(robot.pos.x, robot.pos.y);
                        robot.pos = robot.pos.add(dir.unit().scale(cfg.speed * dt()));
                        emitRobotDust(robot.pos.sub(beforePos));
                        didMove = true;
                    }
                }
            }
        } else {
            robot.path = [];
        }

        if (canUseAmbientAnimation && !didShoot) {
            setRobotAnimation(robot, didMove ? "move" : "idle");
        }

        if (robot.attackCooldown > 0) {
            robot.attackCooldown -= dt();
        } else if (distToPlayer <= meleeDist + 2) {
            if (player.takeDamage) {
                const didDamage = player.takeDamage(cfg.attackDamage);
                const knockbackDir = playerPos.sub(robotPos).unit();
                if (didDamage && player.applyKnockback) {
                    player.applyKnockback(knockbackDir, cfg.attackKnockback || 180);
                }
                if (robot.hurtLock <= 0) {
                    robot.attackLock = WHEEL_BOT_ATTACK_LOCK;
                    setRobotAnimation(robot, "charge");
                    playAt(AUDIO_SOUNDS.robotAttack, robot.pos);
                }
                robot.attackCooldown = cfg.attackCooldown;
            }
        }
    });

    return robot;
}
